import React, { useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useTranslation } from "react-i18next";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  width?: number | string;
  align?: "right" | "left" | "center";
  format?: (value: any, row?: any) => string | React.ReactNode;
  sortable?: boolean;
}

interface EnhancedTableProps {
  columns: Column[];
  data: any[];
  title?: string;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  searchFields?: string[];
  filterFields?: { field: string; options: string[] }[];
  hideSearch?: boolean;
  externalSearchTerm?: string;
  onRowClick?: (row: any) => void;
  actions?: (row: any) => React.ReactNode;
  // External pagination props
  page?: number;
  rowsPerPage?: number;
  totalElements?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  enablePagination?: boolean;
  /** When true, show skeleton rows instead of data. */
  loading?: boolean;
  /** Optional override for how many skeleton rows to show. */
  skeletonRowCount?: number;
  /** Compact styling for embedded tables (e.g. Advance Search modal). */
  tableVariant?: "default" | "compact";
  // Styling props
  cardProps?: {
    elevation?: number;
    sx?: any;
  };
  // Checkbox selection props
  /** When true, shows a checkbox column for row selection */
  selectable?: boolean;
  /** Array of currently selected rows (controlled) */
  selectedRows?: any[];
  /** Callback fired when selection changes */
  onSelectionChange?: (selectedRows: any[]) => void;
  /** Function to get a unique ID from each row. Defaults to row index. */
  getRowId?: (row: any, index?: number) => any;
  /** Max number of rows that can be selected */
  maxSelection?: number;
  /** Per-row function: return false to disable that row's checkbox */
  isRowSelectable?: (row: any) => boolean;
  /** Per-row tooltip text on the checkbox */
  selectTooltip?: (row: any) => string;
  /** Override default select-all handler (use for cross-page selection) */
  onSelectAll?: (checked: boolean) => void;
  /** Override the header checkbox checked state */
  selectAllChecked?: boolean;
  /** Override the header checkbox indeterminate state */
  selectAllIndeterminate?: boolean;
  /** Optional max height for table vertical scrolling (defaults to 440, set to 'none' to disable scroller) */
  maxHeight?: number | string;
}

const EnhancedTable: React.FC<EnhancedTableProps> = ({
  columns,
  data,
  title,
  leftActions,
  rightActions,
  searchFields = [],
  filterFields = [],
  hideSearch = false,
  externalSearchTerm = "",
  onRowClick,
  actions,
  // External pagination props
  page: externalPage,
  rowsPerPage: externalRowsPerPage,
  totalElements,
  onPageChange: externalOnPageChange,
  onRowsPerPageChange: externalOnRowsPerPageChange,
  enablePagination = false,
  loading = false,
  skeletonRowCount,
  tableVariant = "default",
  maxHeight = 440,
  // Styling props
  cardProps = {},
  // Checkbox selection props
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  getRowId = (_row: any, index?: number) => index ?? 0,
  maxSelection,
  isRowSelectable,
  selectTooltip,
  onSelectAll,
  selectAllChecked,
  selectAllIndeterminate,
}) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<{ [key: string]: string }>({});

  const rowsPerPage = enablePagination && externalRowsPerPage !== undefined ? externalRowsPerPage : internalRowsPerPage;
  const effectiveSkeletonRows = skeletonRowCount ?? rowsPerPage;

  // Compute filteredData BEFORE page so we can clamp internalPage to a valid range
  const filteredData = data.filter((row) => {
    const matchesFilters = Object.entries(filters).every(
      ([field, value]) => value === "" || String(row[field]) === value
    );

    const effectiveSearchTerm = hideSearch ? externalSearchTerm : searchTerm;
    if (searchFields.length === 0 || effectiveSearchTerm === "") {
      return matchesFilters;
    }

    const searchLower = effectiveSearchTerm.toLowerCase().trim();
    const isNumberSearch = /^\d+$/.test(searchLower);

    if (isNumberSearch && searchFields.includes('className')) {
      const classNameValue = String(row['className'] || '').toLowerCase().trim();
      const classPattern = new RegExp(`^${searchLower}(st|nd|rd|th)`, 'i');
      const matches = classPattern.test(classNameValue);
      return matches && matchesFilters;
    }

    const matchesSearch = searchFields.some((field, fieldIndex) => {
      const fieldValue = String(row[field] || '').toLowerCase().trim();
      if (searchLower.length <= 2) {
        // For short queries, only match the first search field (title/name) by prefix
        if (fieldIndex !== 0) return false;
        return fieldValue.startsWith(searchLower) || 
               fieldValue.split(/[\s_+,-]+/).some(word => word.startsWith(searchLower));
      }
      return fieldValue.includes(searchLower);
    });

    return matchesSearch && matchesFilters;
  });

  // Clamp internalPage to valid range — this is the key fix for cross-page search.
  // When search reduces filteredData, internalPage may point beyond the last page.
  // Instead of waiting for an async useEffect reset, we clamp synchronously here
  // so the correct page is always used during this render.
  const clampedPage = !enablePagination && filteredData.length > 0
    ? Math.min(internalPage, Math.max(0, Math.ceil(filteredData.length / rowsPerPage) - 1))
    : internalPage;

  const page = enablePagination && externalPage !== undefined ? externalPage : clampedPage;

  const handleChangePage = (event: unknown, newPage: number) => {
    if (enablePagination && externalOnPageChange) {
      externalOnPageChange(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let scrollContainer: Element = document.documentElement;
    let node = cardRef.current?.parentElement ?? null;
    while (node && node !== document.documentElement) {
      const { overflow, overflowY } = window.getComputedStyle(node);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        scrollContainer = node; break;
      }
      node = node.parentElement;
    }
    const savedScroll = scrollContainer.scrollTop;
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (enablePagination && externalOnRowsPerPageChange) {
      externalOnRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(newRowsPerPage);
      setInternalPage(0);
    }
    requestAnimationFrame(() => requestAnimationFrame(() => { scrollContainer.scrollTop = savedScroll; }));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setInternalPage(0);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setInternalPage(0);
  };

  const paginatedData = enablePagination
    ? filteredData
    : filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

  // Checkbox selection helpers
  const resolveId = (row: any, index: number) => getRowId(row, index);

  const isRowSelected = (row: any, index: number) => {
    const id = resolveId(row, index);
    return selectedRows.some((r: any, i: number) => resolveId(r, i) === id);
  };

  const selectablePageRows = paginatedData.filter((r: any) => !isRowSelectable || isRowSelectable(r));
  const allPageSelected = selectAllChecked !== undefined
    ? selectAllChecked
    : selectablePageRows.length > 0 && selectablePageRows.every((row: any, i: number) => isRowSelected(row, i));
  const somePageSelected = selectAllIndeterminate !== undefined
    ? selectAllIndeterminate
    : selectablePageRows.some((row: any, i: number) => isRowSelected(row, i)) && !allPageSelected;

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onSelectAll) {
      onSelectAll(event.target.checked);
      return;
    }
    if (!onSelectionChange) return;
    if (event.target.checked) {
      const newSelected = [...selectedRows];
      paginatedData.forEach((row: any) => {
        if (isRowSelectable && !isRowSelectable(row)) return;
        const id = getRowId(row);
        const alreadySelected = selectedRows.some((r: any) => getRowId(r) === id);
        if (!alreadySelected) {
          if (!maxSelection || newSelected.length < maxSelection) newSelected.push(row);
        }
      });
      onSelectionChange(newSelected);
    } else {
      const currentPageIds = paginatedData.map((r: any) => getRowId(r));
      onSelectionChange(selectedRows.filter((r: any) => !currentPageIds.includes(getRowId(r))));
    }
  };

  const handleRowSelect = (row: any) => {
    if (!onSelectionChange) return;
    const id = getRowId(row);
    const alreadySelected = selectedRows.some((r: any) => getRowId(r) === id);
    if (alreadySelected) {
      onSelectionChange(selectedRows.filter((r: any) => getRowId(r) !== id));
    } else {
      if (maxSelection && selectedRows.length >= maxSelection) return;
      onSelectionChange([...selectedRows, row]);
    }
  };

  const isCompact = tableVariant === "compact";
  const compactHeaderSx = {
    py: "8px",
    px: "10px",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
    backgroundColor: "rgba(48, 146, 227, 0.92)",
    color: "#fff",
    borderBottom: "none",
  };

  return (
    <Card
      ref={cardRef}
      elevation={cardProps.elevation !== undefined ? cardProps.elevation : 3}
      sx={{
        width: "100%",
        mb: isCompact ? 0 : 3,
        mt: isCompact ? 0 : 1.5,
        ...(isCompact && {
          border: "none",
          boxShadow: "none",
          "& .MuiCardContent-root": { p: 0, "&:last-child": { pb: 0 } },
        }),
        ...cardProps.sx,
      }}
    >
      <CardContent>
        {title && (
          <Typography variant="h6" gutterBottom component="div">
            {title}
          </Typography>
        )}

        {(leftActions || rightActions || (!hideSearch && searchFields.length > 0) || filterFields.length > 0) && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: leftActions ? "space-between" : "flex-end",
              alignItems: "center",
            }}
          >
            {leftActions && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {leftActions}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {rightActions && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {rightActions}
                </Box>
              )}
              {!hideSearch && searchFields.length > 0 && (
                <TextField
                  size="small"
                  placeholder={t("search")}
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
              )}

              {filterFields.map(({ field, options }) => (
                <FormControl key={field} size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>{t(field)}</InputLabel>
                  <Select
                    value={filters[field] || ""}
                    label={t(field)}
                    onChange={(e) => handleFilterChange(field, e.target.value)}
                  >
                    <MenuItem value="">{t("all")}</MenuItem>
                    {options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Box>
          </Box>
        )}

        <TableContainer
          component={Paper}
          sx={{
            overflowX: 'auto',
            maxHeight: isCompact ? "none" : maxHeight,
            ...(isCompact && {
              boxShadow: "none",
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
              overflow: "hidden",
            }),
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              ...(isCompact && {
                tableLayout: "fixed",
                width: "100%",
                "& .MuiTableCell-body": {
                  padding: "8px 10px",
                  fontSize: "12px",
                }
              })
            }}
          >
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableCell
                    align="center"
                    sx={{ backgroundColor: "primary.light", width: 50, py: 0.8 }}
                  >
                    <Checkbox
                      size="small"
                      checked={allPageSelected}
                      indeterminate={somePageSelected}
                      onChange={handleSelectAllChange}
                      sx={{ color: "white", "&.Mui-checked": { color: "white" }, "&.MuiCheckbox-indeterminate": { color: "white" }, p: '4px' }}
                    />
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: isCompact ? "rgba(48, 146, 227, 0.92)" : "primary.light",
                    color: "white",
                    width: isCompact ? 50 : 75,
                    py: isCompact ? undefined : 0.8,
                    fontSize: isCompact ? undefined : "0.95rem",
                    ...(isCompact && {
                      ...compactHeaderSx,
                      borderTopLeftRadius: "8px",
                    }),
                  }}
                >
                  {t("s_no")}
                </TableCell>
                {columns.map((column, columnIndex) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth, width: column.width }}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: isCompact ? "rgba(48, 146, 227, 0.92)" : "primary.light",
                      color: "white",
                      py: isCompact ? undefined : 0.8,
                      fontSize: isCompact ? undefined : "0.95rem",
                      ...(isCompact && {
                        ...compactHeaderSx,
                        ...(columnIndex === columns.length - 1 &&
                          !actions && { borderTopRightRadius: "8px" }),
                      }),
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                {actions && (
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: isCompact ? "rgba(48, 146, 227, 0.92)" : "primary.light",
                      color: "white",
                      py: isCompact ? undefined : 0.8,
                      fontSize: isCompact ? undefined : "0.95rem",
                      width: isCompact ? 80 : undefined,
                      ...(isCompact && {
                        ...compactHeaderSx,
                        borderTopRightRadius: "8px",
                      }),
                    }}
                  >
                    {t("actions")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: effectiveSkeletonRows }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    {selectable && (
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Skeleton variant="rounded" width={18} height={18} />
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Skeleton variant="text" width={24} />
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell key={`${column.id}-s-${idx}`} align={column.align} sx={{ py: 0.5 }}>
                        <Skeleton
                          variant="text"
                          width="80%"
                          height={22}
                          sx={{ mx: column.align === "right" ? "auto" : 0 }}
                        />
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Skeleton variant="rounded" width={64} height={28} />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0) + 1}
                    align="center"
                    sx={
                      isCompact
                        ? { py: "20px", fontSize: "13px", color: "#9CA3AF", border: 0 }
                        : undefined
                    }
                  >
                    {isCompact && (
                      <InboxOutlinedIcon sx={{ display: "block", mx: "auto", mb: 1, fontSize: 28, color: "#9CA3AF" }} />
                    )}
                    {t("no_data_found")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => {
                  const isSelected = selectable && isRowSelected(row, index);
                  const rowDisabled = selectable && isRowSelectable ? !isRowSelectable(row) : false;
                  const tooltip = selectable && selectTooltip ? selectTooltip(row) : '';
                  return (
                    <TableRow
                      hover
                      key={index}
                      selected={isSelected}
                      onClick={() => onRowClick && onRowClick(row)}
                      sx={{ cursor: onRowClick ? "pointer" : "default" }}
                    >
                      {selectable && (
                        <TableCell
                          align="center"
                          sx={{ py: 0.5 }}
                          onClick={(e) => { e.stopPropagation(); if (!rowDisabled) handleRowSelect(row); }}
                        >
                          <Tooltip title={tooltip} disableHoverListener={!tooltip}>
                            <span>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={rowDisabled}
                                sx={{ p: '4px' }}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        {index + 1 + page * rowsPerPage}
                      </TableCell>
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align} sx={{ py: 0.5 }}>
                            {column.format ? column.format(value, row) : value}
                          </TableCell>
                        );
                      })}
                      {actions && (
                        <TableCell
                          sx={{ whiteSpace: "nowrap", py: 0.5, ...(isCompact && { width: 80 }) }}
                          align="center"
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        >
                          {actions(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isCompact && paginatedData.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={enablePagination && totalElements !== undefined ? totalElements : filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default EnhancedTable;
